import * as v from 'valibot'

// valibot, not zod, and deliberately: these are FORM schemas. They are parsed on user input a handful
// of times per interaction, and they ship to a browser — where valibot's runtime is ~1.7 KB against
// zod's ~20 KB for identical Standard-Schema output and identical messages (measured on the login
// schema: 1.5 KB vs 20.5 KB). The server reaches this module only through `firstIncompleteCompanyStep`
// below, which is a function call, so one definition still serves both sides.
//
// Messages come from the global valibot message table (`apps/app/src/lib/form-i18n.ts`), so nothing
// here carries a translation function and every schema can be exported as-is.

export const registerSchema = v.object({
  email: v.pipe(v.string(), v.email()),
  password: v.pipe(v.string(), v.minLength(1)),
  termsOfUse: v.literal(true),
})

// Step 1 of the wizard — the company's own details (logo, name, size, sector).
export const companyDetailsSchema = v.object({
  image: v.union([v.file(), v.undefined(), v.null()]),
  name: v.pipe(v.string(), v.minLength(1)),
  employees: v.number(),
  sector: v.number(),
})

// Step 2 of the wizard — where the company is based.
export const companyLocationSchema = v.object({
  country: v.pipe(v.string(), v.minLength(1)),
  province: v.pipe(v.string(), v.minLength(1)),
  city: v.pipe(v.string(), v.minLength(1)),
  // Optional (no minLength) so the field renders an "Opcional" marker, like the applicant location step.
  cp: v.string(),
})

export const companyInfoSchema = v.object({
  ...companyDetailsSchema.entries,
  ...companyLocationSchema.entries,
})

// Step 3 of the wizard — who we reach out to.
export const contactInfoSchema = v.object({
  contactFirstname: v.pipe(v.string(), v.minLength(1)),
  contactLastname: v.pipe(v.string(), v.minLength(1)),
  // Required: the company must give a contact phone (shows "*" / "Requerido", not "Optional").
  contactPhone: v.pipe(v.string(), v.minLength(1)),
})

const _schema = v.object({
  ...registerSchema.entries,
  ...companyInfoSchema.entries,
  ...contactInfoSchema.entries,
})

export type InitialData = Omit<Partial<v.InferInput<typeof _schema>>, 'image'> & { image?: string | null }

// The persisted company fields the registration wizard collects across its steps. The company name
// is stored as the user's `firstname`, surfaced as `name`.
export type CompanyUser = {
  name?: string | null
  employees?: number | null
  sector?: number | null
  country?: string | null
  province?: string | null
  city?: string | null
  cp?: string | null
  contactFirstname?: string | null
  contactLastname?: string | null
  contactPhone?: string | null
}

// First wizard step (details → location → contact) whose data doesn't satisfy that step's schema,
// or null when registration is complete. Mirrors firstIncompleteProfileStep and the server's
// fullyRegisteredCompanyConditions, so a half-registered company resumes where it left off.
export function firstIncompleteCompanyStep(user: CompanyUser): 'details' | 'location' | 'contact' | null {
  // `image` is optional in the schema (a saved company has a CDN url, not a File), so omit it.
  if (
    !v.safeParse(companyDetailsSchema, {
      image: undefined,
      name: user.name,
      employees: user.employees,
      sector: user.sector,
    }).success
  )
    return 'details'

  if (
    !v.safeParse(companyLocationSchema, {
      country: user.country,
      province: user.province,
      city: user.city,
      cp: user.cp ?? '',
    }).success
  )
    return 'location'

  if (
    !v.safeParse(contactInfoSchema, {
      contactFirstname: user.contactFirstname,
      contactLastname: user.contactLastname,
      contactPhone: user.contactPhone,
    }).success
  )
    return 'contact'

  return null
}
